import {
  FluidAction,
  FluidButtonConfig,
  FluidButtonType,
  FluidColorScheme,
  FluidContextMenuItemType,
  FluidContextMenuSection,
  FluidDataLayerComponentConfiguration,
  FluidElementType,
  FluidIconConfig,
  FluidIconKey,
  FluidMenuItem,
  FluidMenuType,
  FluidTextOverflow,
  FluidTheme,
  FluidThemeInterface,
  isNullOrUndefined,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { propSync } from '../../utils/logging/deprecation.utils';
import { themeVar } from '../../utils/themes/theme.variables';
import { Utils } from '../../utils/utils';
import {
  FluidDataLayerUtils,
  sendEvent,
} from '../fluid-data-layer/fluid-data-layer.utils';
import {
  cloneMenuItems,
  hasSubmenu,
} from '../fluid-tree-menu/utils/fluid-tree-menu.utils';

/**
 * @displayName Dropdown Button
 *
 * @contextData FLUID Dropdown Button is exactly what it sounds like - a FLUID Button that displays a dropdown navigation menu when clicked. Dropdown Button utilizes FLUID Tree Menu under the hood, giving it support for nested menu items, expandable submenus, section titles, dividers, & more (see Tree Menu documentation for more information).
 * <br/><br/>
 * Dropdown Button should primarily be used for internal, external, or in-page navigation, but can also be used for contextual actions or controls. It should always be related to nearby page content, and should limit the number of menu items displayed to a maximum of 10-15.
 *
 * @slot icon-before - Named slot for icon displayed to the left of the Button's label
 *
 * @analyticsAware ['linkClicked']
 */
@Component({
  tag: 'fluid-dropdown-button',
  styleUrl: 'fluid-dropdown-button.scss',
  shadow: true,
})
export class FluidDropdownButton
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Reference to the root Context Menu component.
   */
  private _contextMenuRef: HTMLFluidContextMenuElement;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidDropdownButtonElement;

  // ================================================================ //
  // -- States

  /**
   * Boolean that tracks the dropdown menu's open/closed state. Used
   * to "flip" the Button's chevron Icon.
   */
  @State() _menuOpen = false;

  /**
   * Stores clone of "menuItems" Prop OR "links" Prop with all
   * FluidAction objects converted to FluidMenuItem objects.
   */
  @State() _menuItems: FluidMenuItem[];

  /**
   * Stores Context Menu menu section with Slot for embedded Tree Menu.
   */
  @State() _menuSections: FluidContextMenuSection[];

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Unique "id" attribute for the dropdown menu container.
   */
  @Prop({ mutable: true }) menuId: string = generateUniqueId();

  /**
   * Array of FluidMenuItems that populates the dropdown menu.
   */
  @Prop({ mutable: true }) menuItems: FluidMenuItem[] = [];

  @Watch('menuItems')
  @Watch('links')
  onMenuItemsChange() {
    this.initializeMenuItems();
  }

  /**
   * Text for the Button label.
   */
  @Prop({ mutable: true }) label: string;

  /**
   * Sets the color & style of the Button.
   */
  @Prop({ mutable: true }) type: FluidButtonType = FluidButtonType.TERTIARY;

  @Watch('type')
  onTypeChange() {
    propSync(this, 'type', 'color', true);
  }

  /**
   * NOTE: This Prop applies to "type: ghost" Buttons ONLY & will be
   * ignored for all other Button types. Sets a Ghost Button's text color
   * to "light" or "dark" for increased contrast when displayed on a color
   * background. For example, if your Ghost Button is displayed on a DARK
   * BLUE background, set this Prop's value to "light" so that the label
   * color will be white (instead of the default blue). If your Ghost
   * Button is displayed on a YELLOW background, set this Prop's value to
   * "dark" so that the label will be dark gray (instead of the default
   * blue). Do NOT change this Prop's value if your Ghost Button is
   * displayed on a WHITE background. See FLUID Button's "Examples" tab
   * for more info.
   */
  @Prop({ mutable: true }) colorScheme: FluidColorScheme =
    FluidColorScheme.DEFAULT;

  /**
   * If present, renders an Icon to the left of the Button label.
   * NOTE: "iconAfter" is automatically set to "chevron_down" Icon
   * that indicates whether the dropdown menu is open or closed.
   */
  @Prop({ mutable: true }) iconBefore: FluidIconConfig;

  /**
   * When true, hovering over the Button will open the dropdown menu
   * and moving the mouse out of the component's bounds will close it.
   * NOTE: this Prop DISABLES click events on the Button (i.e. clicking
   * the Button will NOT open/close the menu), but does NOT affect
   * keyboard events (i.e. "enter" or "space" WILL open the menu).
   */
  @Prop({ mutable: true }) openOnHover: boolean;

  /**
   * Changes the dropdown menu's CSS "position" property from "fixed"
   * (default) to "absolute". NOTE: if the dropdown menu isn't displaying
   * correctly, try setting this Prop's value to "true".
   */
  @Prop({ mutable: true }) absolutePosition: boolean;

  /**
   * Sets a maximum height on the dropdown menu & allows vertical
   * scrolling for content overflow. Accepts any string value that is
   * valid for CSS "max-height" property.
   */
  @Prop({ mutable: true }) maxHeight: string;

  /**
   * When true, Button & dropdown menu will be disabled.
   */
  @Prop({ mutable: true }) disabled: boolean;

  /**
   * Removes the Button's default right AND bottom margins.
   */
  @Prop({ mutable: true }) removeMargin = false;

  /**
   * Removes the Button's default right margin.
   */
  @Prop({ mutable: true }) removeHorizontalMargin = false;

  /**
   * Removes the Button's default bottom margin.
   */
  @Prop({ mutable: true }) removeVerticalMargin = false;

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when a menu item in the dropdown menu is clicked
   * (NOTE: does NOT include "submenu buttons"). Contains the menu
   * item's FluidMenuItem object.
   */
  @Event() linkClicked: EventEmitter<FluidMenuItem>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: setZIndex - set component z-index relative to global base value
   * @action: getButtonLabel - sets "label" Prop to "title" attribute value
   * @action: propSync - keeps deprecated "color" Prop in-sync with "type" Prop
   */
  connectedCallback(): void {
    Utils.setGlobalTheme(this);
    Utils.setZIndex(this.host);
    this.getButtonLabel();
    Utils.watchAttr('title', this.host, () => this.getButtonLabel(true));
    useI18n.bind(this)({
      init: () => (this._i18nBound = true),
    });
    propSync(this, 'type', 'color', isNullOrUndefined(this.color));
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called once just after the component is first connected to the DOM.
   */
  componentWillLoad() {
    this.initializeMenuItems();
    this.initializeContextMenu();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action: setupAnalytics - FLUID Data Layer configuration setup
   */
  componentDidLoad() {
    this.setupAnalytics();
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Listener for "menuItemClick" Events on embedded Tree Menu. Emits
   * "linkClicked" Event containing the FluidMenuItem object that was
   * selected & closes the dropdown menu. If menu item was converted
   * from FluidAction & has "action" property set, performs given
   * "action" function as well.
   */
  @Listen('menuItemClick')
  onMenuItemClick(event: CustomEvent<FluidMenuItem>) {
    const menuItem = event.detail;

    !!menuItem?.action &&
      menuItem.action instanceof Function &&
      menuItem.action();
    this.linkClicked.emit(menuItem);
    sendEvent(this.host, this.parentLayerId, 'linkClicked', menuItem);
    this.toggleMenu(false);
  }

  /**
   * Listener for "contextMenuToggled" Events on the embedded Context
   * Menu component. Sets internal "_menuOpen" variable to reflect the
   * dropdown menu's open/closed state.
   */
  @Listen('contextMenuToggled')
  onContextMenuToggled(event: CustomEvent<boolean>) {
    this._menuOpen = event.detail;
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Toggles the dropdown menu open/closed.
   */
  @Method()
  async toggleMenu(open = !this._menuOpen) {
    if (this.disabled || this._menuOpen === open) return;
    this._menuOpen = open;
    this._contextMenuRef.toggleMenu(this._menuOpen).then();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * If "title" attribute is provided instead of "label" Prop, sets
   * "label" to "title" attribute's value.
   */
  getButtonLabel(update = false): void {
    if ((!update && !!this.label) || !this.host.title) return;
    this.label = this.host.title;
  }

  /**
   * If "links" Prop is provided, maps each FluidAction object to a
   * FluidMenuItem object & outputs console deprecation warning. If
   * "menuItems" Prop is provided, sets internal "_menuItems" State.
   * Also sets all menu items with "route" property to render as anchor
   * tags (instead of button tags).
   */
  initializeMenuItems(): void {
    if (this.links?.length) {
      Utils.deprecationWarning(
        this.host,
        'Prop',
        'links',
        'menuItems',
        'FluidMenuItem[]',
        'FluidAction[]'
      );
      this._menuItems = this.links.map(
        (action) => ({ ...action, id: action.identifier } as FluidMenuItem)
      );
    } else {
      this._menuItems = cloneMenuItems(this.menuItems);
    }

    this.setElementTypes(this._menuItems);
  }

  /**
   * Sets "elementType" to "anchor" for each FluidMenuItem object in the
   * "menuItems" array that has a "route" property provided. NOTE: this
   * is for consistency with original Dropdown Button functionality that
   * rendered EVERY link as an anchor tag.
   * @param items - current array of FluidMenuItem objects being traversed
   */
  setElementTypes(items: FluidMenuItem[]): void {
    items.forEach((item) => {
      if (!!item.route && !item.elementType) {
        item.elementType = FluidElementType.ANCHOR;
      }
      hasSubmenu(item) && this.setElementTypes(item.submenu);
    });
  }

  /**
   * Creates the Context Menu "menu section" that holds the embedded Tree
   * Menu & sets "sectionHeight" (if "maxHeight" Prop is provided).
   */
  initializeContextMenu(): void {
    this._menuSections = [
      {
        type: FluidContextMenuItemType.SLOT,
        slotName: 'dropdown-button-tree-menu',
        removeSlotPadding: true,
        sectionHeight: !!this.maxHeight && this.maxHeight,
      },
    ];
  }

  /**
   * Returns FluidButtonConfig object used to render embedded FLUID Button.
   * If deprecated "icon" Prop has been provided, outputs deprecation
   * warning to the console.
   */
  getButtonConfig(): FluidButtonConfig {
    !!this.icon &&
      Utils.deprecationWarning(
        this.host,
        'Prop',
        'icon',
        'iconBefore',
        'FluidIconConfig',
        'FluidIconKey'
      );

    const iconAfterConfig = themeVar<FluidIconConfig>(
      this.theme,
      'icons.dropdown_button.chevron'
    );

    return {
      label:
        this._i18nStore?.translate(this.fluidTranslate?.label) || this.label,
      type: this.type,
      colorScheme: this.colorScheme,
      disabled: this.disabled,
      removeMargin: true,
      iconBefore:
        this.iconBefore ?? (this.icon ? { key: this.icon } : undefined),
      iconAfter: {
        key: FluidIconKey.CHEVRON_DOWN,
        flipVertical: this._menuOpen,
        ...iconAfterConfig,
      },
      theme: this.theme,
      overrideGlobalTheme: true,
    };
  }

  /**
   * Returns true if Dropdown Button has slotted "icon-before", false
   * if not. Prevents unnecessary margin on left side of Button label
   * caused by empty icon Slot.
   */
  hasSlottedIconBefore(): boolean {
    return (
      !this.iconBefore &&
      !this.icon &&
      !!this.host.querySelector('[slot="icon-before"]')
    );
  }

  // ================================================================ //
  // -- Render (Partials)

  /**
   * Recursively finds all menu items with Icons using "slotName" Prop
   * & renders "pass-through" slots to "tunnel" slotted icon children
   * into their appropriate menu items.
   * @param menuItems - the current array of menu items to iterate through
   */
  renderMenuItemIconSlots(menuItems: FluidMenuItem[]): HTMLElement[] {
    return menuItems.reduce((acc: HTMLElement[], item) => {
      const iconBeforeSlotName = item.iconBefore?.slotName;
      const iconAfterSlotName = item.iconAfter?.slotName;

      if (iconBeforeSlotName) {
        acc = [
          ...acc,
          <slot name={iconBeforeSlotName} slot={iconBeforeSlotName} />,
        ];
      }
      if (iconAfterSlotName) {
        acc = [
          ...acc,
          <slot name={iconAfterSlotName} slot={iconAfterSlotName} />,
        ];
      }
      if (hasSubmenu(item)) {
        acc = [...acc, ...this.renderMenuItemIconSlots(item.submenu)];
      }

      return acc;
    }, []);
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      this._i18nBound && (
        <fluid-context-menu
          ref={(el) => (this._contextMenuRef = el)}
          menuId={this.menuId}
          menuSections={this._menuSections}
          disabled={this.disabled}
          openOnHover={this.openOnHover}
          absolutePosition={this.absolutePosition}
          outsideClickClose={true}
          asWrapper={true}
          zIndex={this.zIndex}
          theme={this.theme}
          overrideGlobalTheme={true}
          class={{
            [`fluid-dropdown-button theme-${this.theme}`]: true,
            disabled: this.disabled,
            'no-margin': this.removeMargin,
            'no-vertical-margin': this.removeVerticalMargin,
            'no-horizontal-margin': this.removeHorizontalMargin,
          }}>
          <fluid-button config={this.getButtonConfig()}>
            {this.hasSlottedIconBefore() && (
              <slot name='icon-before' slot='icon-before' />
            )}
          </fluid-button>
          <fluid-tree-menu
            slot='dropdown-button-tree-menu'
            config={{
              menuItems: this._menuItems,
              fluidTranslate: this.fluidTranslate,
              menuType: FluidMenuType.DROPDOWN,
              textOverflow: FluidTextOverflow.ELLIPSIS,
              theme: this.theme,
              overrideGlobalTheme: true,
            }}>
            {this.renderMenuItemIconSlots(this._menuItems)}
          </fluid-tree-menu>
        </fluid-context-menu>
      )
    );
  }

  // ================================================================ //
  // -- Internal Props
  //
  // -- Note: This is not a public API - these properties
  // -- should only be used internally by FLUID components.

  /**
   * Overrides default CSS z-index value for dropdown menu.
   * @internal
   */
  @Prop({ mutable: true }) zIndex: number;

  /**
   * If the element should be configured at a component level for the FLUID data layer,
   * this configuration dictates how it should be instrumented
   * @internal
   * @experimental
   */
  @Prop({ mutable: true })
  analyticsConfig: FluidDataLayerComponentConfiguration;
  parentLayerId: string;
  setupAnalytics() {
    this.parentLayerId = FluidDataLayerUtils.getClosestDataLayer(this.host)?.id;
  }

  // ================================================================ //
  // -- Deprecated Props

  /**
   * DEPRECATED: "color" is now deprecated. Instead, use the "type" Prop
   * (same functionality, just a different naming convention) -- Style
   * settings applied to the Button.
   * @deprecated
   */
  @Prop({ mutable: true }) color: FluidButtonType;

  @Watch('color')
  onColorChange() {
    propSync(this, 'type', 'color');
  }

  /**
   * DEPRECATED: "links" is now deprecated. Use "menuItems" Prop instead.
   * -- Array of FluidAction objects that populate the dropdown menu with
   * menu items. Values from this Prop are now mapped to "menuItems" Prop.
   * @deprecated
   */
  @Prop({ mutable: true }) links: FluidAction[] = [];

  /**
   * DEPRECATED: "icon" is now deprecated. Use "iconBefore" Prop instead.
   * If present, renders an Icon BEFORE the Button label (NOTE: "iconAfter"
   * is automatically set to "chevron" Icon that indicates whether dropdown
   * menu is open or closed).
   * @deprecated
   */
  @Prop({ mutable: true }) icon: FluidIconKey;

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: { label?: string };
  private _translateId: string;
  private _i18nStore: FluidI18nStore | undefined;
  @State() _i18nBound = false;
}
